import React, { useState, useEffect } from 'react';
import { useSocial } from '../../social/SocialContext';
import { useAuth } from '../../auth/AuthContext';
import { Save, CheckCircle, XCircle, Loader2, Link as LinkIcon } from 'lucide-react';
import { MediaUpload } from '../../../components/ui/MediaUpload';

export const SocialProfilePage: React.FC = () => {
    const { user } = useAuth();
    const { profile, loading, createProfile, updateProfile, checkHandle } = useSocial(); // Added updateProfile
    
    // Form State
    const [handle, setHandle] = useState('');
    const [displayName, setDisplayName] = useState(user?.displayName || '');
    const [bio, setBio] = useState('');
    const [avatarUrl, setAvatarUrl] = useState(''); // New state
    const [coverUrl, setCoverUrl] = useState('');   // New state
    
    // Status State
    const [isChecking, setIsChecking] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize form when profile loads
    useEffect(() => {
        if (profile) {
            setHandle(profile.handle);
            setDisplayName(profile.displayName);
            setBio(profile.bio || '');
            setAvatarUrl(profile.avatarUrl || ''); // Initialize avatar
            setCoverUrl(profile.coverUrl || '');   // Initialize cover
        }
    }, [profile]);

    // Handle check debounce logic
    useEffect(() => {
        if (!handle || (profile && handle === profile.handle)) {
            setIsAvailable(null);
            return;
        }

        const timeoutId = setTimeout(async () => {
            if (handle.length < 3) return;
            setIsChecking(true);
            try {
                const available = await checkHandle(handle);
                setIsAvailable(available);
            } catch (err) {
                console.error(err);
            } finally {
                setIsChecking(false);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [handle, profile, checkHandle]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const profileData = {
                displayName,
                bio,
                avatarUrl,
                coverUrl
            };

            if (!profile) {
                // Create New Profile
                if (!isAvailable) {
                    throw new Error('Este handle não está disponível.');
                }
                await createProfile({
                    handle: handle.toLowerCase(),
                    ...profileData
                });
            } else {
                // Update Profile
                 await updateProfile(profileData);
                 alert('Perfil atualizado com sucesso!');
            }
        } catch (err: any) {
             console.error(err);
            setError(err.message || 'Erro ao salvar perfil');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-8"> {/* Increased max-width */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Meu Perfil Social</h1>
                <p className="text-gray-500">
                    {profile 
                        ? 'Gerencie como você aparece para outros pescadores.' 
                        : 'Crie sua identidade única na comunidade Nossa Pesca.'}
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 md:p-8 space-y-8">
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        {/* Media Section */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                             {/* Avatar Upload */}
                            <div className="md:col-span-1">
                                <MediaUpload
                                    label="Foto de Perfil"
                                    type="image"
                                    path={`profiles/${handle || 'temp'}/avatar`}
                                    onUploadComplete={setAvatarUrl}
                                    currentUrl={avatarUrl || user?.photoURL || undefined}
                                    className="w-full"
                                />
                            </div>

                             {/* Cover Upload */}
                            <div className="md:col-span-2">
                                <MediaUpload
                                    label="Capa do Perfil"
                                    type="image"
                                    path={`profiles/${handle || 'temp'}/cover`}
                                    onUploadComplete={setCoverUrl}
                                    currentUrl={coverUrl}
                                    className="w-full"
                                />
                            </div>
                        </div>


                        {/* Handle Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Seu Handle (Nome de Usuário) *
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-400 font-bold">@</span>
                                </div>
                                <input
                                    type="text"
                                    required
                                    disabled={!!profile} // Disable handle changing for now
                                    value={handle}
                                    onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                                    className={`w-full pl-8 pr-12 py-3 border rounded-lg font-mono ${
                                        isAvailable === true ? 'border-green-500 focus:ring-green-500' :
                                        isAvailable === false ? 'border-red-500 focus:ring-red-500' :
                                        'border-gray-300 focus:ring-blue-500'
                                    } disabled:bg-gray-50 disabled:text-gray-500`}
                                    placeholder="joao.pescador"
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    {isChecking ? (
                                        <Loader2 className="animate-spin text-blue-500" size={18} />
                                    ) : isAvailable === true ? (
                                        <CheckCircle className="text-green-500" size={20} />
                                    ) : isAvailable === false ? (
                                        <XCircle className="text-red-500" size={20} />
                                    ) : null}
                                </div>
                            </div>
                            {/* Feedback messages */}
                            <div className="min-h-[20px] mt-1">
                                {isAvailable === false && (
                                    <p className="text-xs text-red-500">Este nome de usuário já está em uso.</p>
                                )}
                                {isAvailable === true && (
                                    <p className="text-xs text-green-600">Disponível! Seu link será: nossapesca.com/{handle}</p>
                                )}
                                {profile && (
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <LinkIcon size={10} /> nossapesca.com/{profile.handle}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Display Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nome de Exibição *
                            </label>
                            <input
                                type="text"
                                required
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="João da Silva"
                            />
                        </div>

                        {/* Bio */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bio (Opcional)
                            </label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Apaixonado por tucunarés e pesca esportiva..."
                            />
                        </div>

                        {error && (
                            <div className="p-4 bg-red-50 text-red-700 text-sm rounded-lg">
                                {error}
                            </div>
                        )}

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting || (isAvailable === false && !profile)}
                                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        Salvando...
                                    </>
                                ) : profile ? (
                                    <>
                                        <Save size={20} />
                                        Salvar Alterações
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle size={20} />
                                        Criar Meu Perfil
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
